import { useMemo, useState } from 'react';
import type { MediaItem, MediaType, Profile } from '../../shared/types';
import { PROFILES } from '../../shared/types';
import { PlayIcon } from './Icons';

export type SortMode = 'captured-asc' | 'captured-desc' | 'uploaded-desc' | 'filename';

interface GalleryProps {
  items: MediaItem[];
  onSelect: (item: MediaItem) => void;
}

interface Filters {
  type: 'all' | MediaType;
  uploader: 'all' | Profile;
  sort: SortMode;
}

const dayKey = (item: MediaItem) => {
  const date = new Date(item.capturedAt);

  return Number.isNaN(date.getTime()) ? 'Unknown date' : date.toISOString().slice(0, 10);
};

const dayLabel = (key: string) => {
  if (key === 'Unknown date') return key;

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(`${key}T12:00:00`));
};

export function useFilteredMedia(items: MediaItem[]) {
  const [filters, setFilters] = useState<Filters>({
    type: 'all',
    uploader: 'all',
    sort: 'captured-asc',
  });
  const filtered = useMemo(() => {
    const selected = items.filter(
      (item) =>
        (filters.type === 'all' || item.mediaType === filters.type) &&
        (filters.uploader === 'all' || item.uploader === filters.uploader),
    );

    return selected.sort((left, right) => {
      if (filters.sort === 'filename') return left.name.localeCompare(right.name);
      if (filters.sort === 'uploaded-desc') {
        return Date.parse(right.uploadedAt) - Date.parse(left.uploadedAt);
      }

      const difference = Date.parse(left.capturedAt) - Date.parse(right.capturedAt);

      return filters.sort === 'captured-desc' ? -difference : difference;
    });
  }, [filters, items]);

  return { filters, setFilters, filtered };
}

export function Gallery({ items, onSelect }: GalleryProps) {
  const { filters, setFilters, filtered } = useFilteredMedia(items);
  const groups = useMemo(() => {
    const next = new Map<string, MediaItem[]>();

    for (const item of filtered) {
      const key = dayKey(item);
      next.set(key, [...(next.get(key) ?? []), item]);
    }

    return Array.from(next.entries());
  }, [filtered]);

  return (
    <>
      <section className="gallery-controls" aria-label="Gallery filters">
        <div className="segmented">
          {(['all', 'photo', 'video'] as const).map((type) => (
            <button
              key={type}
              className={filters.type === type ? 'segmented__active' : ''}
              type="button"
              onClick={() => setFilters({ ...filters, type })}
            >
              {type === 'all' ? 'All' : type === 'photo' ? 'Photos' : 'Videos'}
            </button>
          ))}
        </div>

        <div className="gallery-controls__selects">
          <label>
            <span className="sr-only">Filter by uploader</span>
            <select
              value={filters.uploader}
              onChange={(event) =>
                setFilters({ ...filters, uploader: event.target.value as Filters['uploader'] })
              }
            >
              <option value="all">Everyone</option>
              {PROFILES.map((profile) => (
                <option value={profile} key={profile}>
                  {profile}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="sr-only">Sort gallery</span>
            <select
              value={filters.sort}
              onChange={(event) =>
                setFilters({ ...filters, sort: event.target.value as SortMode })
              }
            >
              <option value="captured-asc">Oldest captured</option>
              <option value="captured-desc">Newest captured</option>
              <option value="uploaded-desc">Recently uploaded</option>
              <option value="filename">Filename</option>
            </select>
          </label>
        </div>
      </section>

      {groups.length === 0 ? (
        <section className="empty-gallery">
          <p className="eyebrow">Nothing here yet</p>
          <h2>{items.length ? 'Try another filter.' : 'Be the first to add a memory.'}</h2>
        </section>
      ) : (
        <div className="day-groups">
          {groups.map(([day, dayItems], groupIndex) => (
            <section className="day-group" aria-labelledby={`Day-${day}`} key={day}>
              <header className="day-group__header">
                <p>0{groupIndex + 1}</p>
                <h2 id={`Day-${day}`}>{dayLabel(day)}</h2>
                <span>{dayItems.length}</span>
              </header>
              <div className="contact-sheet">
                {dayItems.map((item) => (
                  <button
                    className="media-tile"
                    type="button"
                    style={
                      {
                        '--media-ratio': item.width && item.height ? item.width / item.height : 1.2,
                      } as React.CSSProperties
                    }
                    onClick={() => onSelect(item)}
                    key={item.id}
                    aria-label={`Open ${item.mediaType}: ${item.name}`}
                  >
                    <img src={item.thumbnailUrl} alt="" loading="lazy" />
                    {item.mediaType === 'video' && (
                      <span className="media-tile__play">
                        <PlayIcon />
                      </span>
                    )}
                    <span className="media-tile__meta">
                      {new Date(item.capturedAt).toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}
